import { SelectionPromotion } from '@teko/cart/v2';
import {
  CheckOutlined,
  ChevronDownOutlined,
  ChevronUpOutlined,
  GiftFilled,
} from '@teko/ds-icon';
import {
  Body,
  Caption,
  Divider,
  Flex,
  Row,
  Shape,
  ThemeProps,
  styled,
} from '@teko/ds-mobile';
import { trackManually } from '@teko/tracking';
import { DividerStyleEnum } from '@teko/types/dist/shared';
import { requestIdleCallback } from '@teko/utils';
import { useProductDetailState } from 'contexts';
import DOMPurify from 'dompurify';
import { t } from 'i18n';
import { useState } from 'react';

interface ISelectionPromotionsProps {
  title: string;
  description?: string;
  promotions?: SelectionPromotion[];
  isExclusivePromotionGroups?: boolean;
}

const MAX_ITEMS_PROMOTIONS = 3;

export const SelectionPromotions = (props: ISelectionPromotionsProps) => {
  const {
    title,
    description,
    promotions = [],
    isExclusivePromotionGroups = false,
  } = props;

  const { product, setSelectedPromotion } = useProductDetailState();
  const [showFullPromotions, setShowFullPromotions] = useState(false);

  const handlePromotionClick = (
    promotion: SelectionPromotion,
    index: number,
  ) => {
    if (!promotion) return;
    if (promotion.isSelected) {
      setSelectedPromotion?.(undefined);
    } else {
      setSelectedPromotion?.(promotion);
    }
    requestIdleCallback(() =>
      trackManually(
        promotion.name as string,
        'optionalPromotion',
        'productDetail',
        JSON.stringify({
          sku: product?.sku,
          action: promotion.isSelected ? 'unselect' : 'select',
          promotionID: promotion.promotionId,
          screenName: 'productDetail',
          index,
        }),
      ),
    );
  };

  const toggleShowFullPromotions = () => {
    setShowFullPromotions((prev) => !prev);
  };

  if (promotions?.length === 0) return null;

  const convertPromotion = showFullPromotions
    ? promotions
    : promotions.slice(0, MAX_ITEMS_PROMOTIONS);

  return (
    <Wrapper
      direction="column"
      gap="s8"
      borderRadius="s"
      padding="s8"
      width="100%"
    >
      <Caption level={2} color="#000000A6">
        {t(title)}:{`${description ? ` (${description})` : ``}`}
      </Caption>
      {convertPromotion?.map((promotion, index) => (
        <PromotionOption
          key={promotion.promotionId || index}
          promotion={promotion}
          onClick={() => handlePromotionClick(promotion, index)}
          isExclusive={!!isExclusivePromotionGroups}
        />
      ))}
      {promotions.length > MAX_ITEMS_PROMOTIONS && (
        <Row align="middle" justify="center" width="100%">
          <SeeMoreView
            direction="row"
            onClick={toggleShowFullPromotions}
            gap="s8"
          >
            <Body level={1} color="textTitle">
              {showFullPromotions
                ? t('Ẩn bớt')
                : t('Xem thêm {{numOfPromotion}} KM khác', {
                    numOfPromotion: promotions.length - MAX_ITEMS_PROMOTIONS,
                  })}
            </Body>
            {showFullPromotions ? (
              <ChevronUpOutlined color="textTitle" size={9} />
            ) : (
              <ChevronDownOutlined color="textTitle" size={9} />
            )}
          </SeeMoreView>
        </Row>
      )}
    </Wrapper>
  );
};

const Wrapper = styled(Flex)`
  background: linear-gradient(
    180deg,
    #f5f5f5 0%,
    rgba(245, 245, 245, 0.6) 100%
  );
`;

const SeeMoreView = styled(Flex)`
  cursor: pointer;
  padding: 6px 0px;
`;

export interface PromotionOptionProps {
  promotion: SelectionPromotion;
  onClick?: () => void;
  isExclusive?: boolean;
}

const PromotionOption = ({
  promotion,
  onClick,
  isExclusive,
}: PromotionOptionProps) => {
  const { isSelectable, isSelected } = promotion;

  return (
    <Shape
      backgroundColor="background"
      borderRadius="m"
      borderStyle="solid"
      borderWeight="l"
      borderColor={isSelected ? 'primary500' : 'border'}
      width="100%"
    >
      <PromotionOptionWrapper
        onClick={isSelectable ? onClick : undefined}
        disable={!isSelectable}
      >
        <Flex
          align="center"
          justify="start"
          padding={['s8', 's12', 's4']}
          gap="s8"
        >
          <GiftFilled
            color={!!isExclusive ? 'primary500' : '#4CB9CC'}
            size={16}
          />
          <PromotionOptionLabel
            level={1}
            color="textTitle"
            width="100%"
            dangerouslySetInnerHTML={{
              __html: DOMPurify?.sanitize?.(promotion.title) || promotion.title,
            }}
          />
        </Flex>
        <Divider
          dividerStyle={DividerStyleEnum.dashed}
          color="#0000000F"
          gap="0"
          border="1px"
        />
        <Flex
          justify="space-between"
          width="100%"
          align="end"
          padding={['s4', 's12', 's8']}
        >
          <Flex direction="column" gap="s4">
            {!!promotion.highlightedDescriptions?.length && (
              <Caption level={1} weight="regular" color="pending700">
                {promotion.highlightedDescriptions[0]}
              </Caption>
            )}
            <ConditionText level={1} color="textPrimary">
              {promotion.description}
            </ConditionText>
            <ExpiredAt level={1} color="textPrimary">
              {`${t('HSD')}: ${new Date(
                promotion.endedAt ?? new Date(),
              ).toLocaleDateString('vi')}`}
            </ExpiredAt>
          </Flex>
          <Flex direction="column" width="min-content">
            <StyledTick isSelected={!!isSelected}>
              <CheckOutlined color="white" size={16} />
            </StyledTick>
          </Flex>
        </Flex>
      </PromotionOptionWrapper>
      {!isSelectable && !isExclusive && (
        <Row justify="start" padding={['s4', 's12']}>
          <Caption level={1}>{t('Không áp dụng cùng Ưu đãi đặc biệt')}</Caption>
        </Row>
      )}
    </Shape>
  );
};

const maxLineCss = (maxLines: number) => `
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${maxLines};
`;

export interface StyledProps extends ThemeProps {
  disable?: boolean;
}

const PromotionOptionWrapper = styled.div(
  ({ disable }: StyledProps) => `
  background-color: white;
  width: 100%;
  border-radius: 8px;
  ${disable && 'border: 1.5px solid #0000000F'};
  cursor: pointer;
  ${disable && `opacity: 0.4; cursor: not-allowed;`}
`,
);

const PromotionOptionLabel = styled(Body)`
  white-space: pre-line;
  img {
    height: 20px;
    width: 20px;
    border-radius: 4px;
    border: 1px solid #0000000f;
    object-fit: contain;
    cursor: zoom-in;
  }
`;

const ConditionText = styled(Caption)`
  ${maxLineCss(2)}
`;

const ExpiredAt = styled(Caption)``;

const StyledTick = styled.div<{ isSelected?: boolean }>(
  ({ isSelected, theme }) => `
    width: 24px;
    height: 24px;
    border-radius: 999px;
    background-color: ${isSelected ? theme?.colors.primary500 : 'white'};
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid ${isSelected ? theme?.colors.primary : '#00000026'};

`,
);
